import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Package, 
  ShoppingCart, 
  Users, 
  LogOut,
  Plus,
  Edit,
  Trash2,
  X,
  DollarSign,
  TrendingUp,
  Key,
  CreditCard,
  Banknote,
  Building2,
  Eye,
  EyeOff,
  MapPin,
  Truck,
  Home
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Meeting point name mapping
const MEETING_POINT_NAMES = {
  'plaza_ciudad_jardin': 'Plaza Ciudad Jardín',
  'parque_del_pueblo': 'Parque del Pueblo',
  'metro_pantitlan': 'Metro Pantitlán',
  'plaza_aragon': 'Plaza Aragón',
  'centro_nezahualcoyotl': 'Centro de Nezahualcóyotl'
};

const AdminDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    image_url: '',
    stock: 100,
    featured: false
  });

  useEffect(() => {
    fetchData();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/products/categories`);
      const cats = response.data.categories || [];
      setCategories(cats.sort());
      // Set default category if not set
      if (cats.length > 0 && !productForm.category) {
        setProductForm(prev => ({ ...prev, category: cats[0] }));
      }
    } catch (error) {
      setCategories([]);
      toast.error('No se pudieron cargar las categorías');
    }
  };

  const handleAddNewCategory = () => {
    if (newCategory.trim()) {
      const trimmedCategory = newCategory.trim();
      if (!categories.includes(trimmedCategory)) {
        setCategories(prev => [...prev, trimmedCategory].sort());
      }
      setProductForm(prev => ({ ...prev, category: trimmedCategory }));
      setNewCategory('');
      setShowNewCategoryInput(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, productsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/products`),
        axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStats(statsRes.data);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(price);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Contraseña actualizada exitosamente');
      setShowPasswordModal(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar la contraseña');
    } finally {
      setChangingPassword(false);
    }
  };

  const openProductModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        name: product.name,
        description: product.description,
        price: product.price.toString(),
        category: product.category,
        image_url: product.image_url,
        stock: product.stock,
        featured: product.featured
      });
      // Ensure the product's category is in the list
      if (!categories.includes(product.category)) {
        setCategories(prev => [...prev, product.category].sort());
      }
    } else {
      setEditingProduct(null);
      setProductForm({
        name: '',
        description: '',
        price: '',
        category: categories[0] || '',
        image_url: '',
        stock: 100,
        featured: false
      });
    }
    setShowNewCategoryInput(false);
    setNewCategory('');
    setShowProductModal(true);
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    
    const productData = {
      ...productForm,
      price: parseFloat(productForm.price),
      stock: parseInt(productForm.stock)
    };

    try {
      if (editingProduct) {
        await axios.put(
          `${API}/admin/products/${editingProduct.id}`,
          productData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Producto actualizado');
      } else {
        await axios.post(
          `${API}/admin/products`,
          productData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Producto creado');
      }
      setShowProductModal(false);
      fetchData();
    } catch (error) {
      toast.error('Error al guardar producto');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('¿Estás seguro de eliminar este producto?')) return;
    
    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Producto eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar producto');
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-64 bg-[#1C1917] text-white fixed left-0 top-0 bottom-0 hidden md:block">
        <div className="p-6">
          <Link to="/">
            <h1 
              className="text-2xl font-light tracking-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Aliglam
            </h1>
          </Link>
          <p className="text-xs text-[#A8A29E] mt-1">Panel de Administración</p>
        </div>

        <nav className="px-4 mt-8 space-y-2">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`admin-nav-item w-full text-left flex items-center space-x-3 ${activeTab === 'dashboard' ? 'active' : ''}`}
            data-testid="nav-dashboard"
          >
            <LayoutDashboard className="w-5 h-5" />
            <span>Dashboard</span>
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`admin-nav-item w-full text-left flex items-center space-x-3 ${activeTab === 'products' ? 'active' : ''}`}
            data-testid="nav-products"
          >
            <Package className="w-5 h-5" />
            <span>Productos</span>
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`admin-nav-item w-full text-left flex items-center space-x-3 ${activeTab === 'orders' ? 'active' : ''}`}
            data-testid="nav-orders"
          >
            <ShoppingCart className="w-5 h-5" />
            <span>Pedidos</span>
          </button>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#BA7B7C] flex items-center justify-center">
              <span className="text-white font-medium">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-[#A8A29E]">Admin</p>
            </div>
          </div>
          <button
            onClick={() => setShowPasswordModal(true)}
            className="w-full flex items-center justify-center space-x-2 py-2 text-sm text-[#A8A29E] hover:text-white transition-colors mb-2"
            data-testid="change-password-btn"
          >
            <Key className="w-4 h-4" />
            <span>Cambiar contraseña</span>
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center space-x-2 py-2 text-sm text-[#A8A29E] hover:text-white transition-colors"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4" />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 md:ml-64 p-6 md:p-8">
        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between mb-6">
          <h1 
            className="text-xl font-light"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Aliglam Admin
          </h1>
          <button onClick={handleLogout} className="text-[#57534E]">
            <LogOut className="w-5 h-5" />
          </button>
        </div>

        {/* Mobile Tabs */}
        <div className="md:hidden flex space-x-4 mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap ${
              activeTab === 'dashboard' ? 'bg-[#BA7B7C] text-white' : 'bg-white text-[#57534E]'
            }`}
          >
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap ${
              activeTab === 'products' ? 'bg-[#BA7B7C] text-white' : 'bg-white text-[#57534E]'
            }`}
          >
            Productos
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap ${
              activeTab === 'orders' ? 'bg-[#BA7B7C] text-white' : 'bg-white text-[#57534E]'
            }`}
          >
            Pedidos
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-2 border-[#BA7B7C] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && (
              <div data-testid="dashboard-content">
                <h2 
                  className="text-3xl font-light text-[#1C1917] mb-8"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Dashboard
                </h2>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
                  <div className="bg-white rounded-2xl p-6 shadow-soft">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-[#A8A29E]">Productos</span>
                      <Package className="w-5 h-5 text-[#BA7B7C]" />
                    </div>
                    <p className="text-3xl font-light text-[#1C1917]" data-testid="stat-products">
                      {stats?.product_count || 0}
                    </p>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-soft">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-[#A8A29E]">Pedidos</span>
                      <ShoppingCart className="w-5 h-5 text-[#BA7B7C]" />
                    </div>
                    <p className="text-3xl font-light text-[#1C1917]" data-testid="stat-orders">
                      {stats?.order_count || 0}
                    </p>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-soft">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-[#A8A29E]">Clientes</span>
                      <Users className="w-5 h-5 text-[#BA7B7C]" />
                    </div>
                    <p className="text-3xl font-light text-[#1C1917]" data-testid="stat-users">
                      {stats?.user_count || 0}
                    </p>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-soft">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-[#A8A29E]">Ingresos</span>
                      <DollarSign className="w-5 h-5 text-[#BA7B7C]" />
                    </div>
                    <p className="text-2xl font-light text-[#1C1917]" data-testid="stat-revenue">
                      {formatPrice(stats?.total_revenue || 0)}
                    </p>
                  </div>
                </div>

                {/* Recent Orders */}
                <div className="bg-white rounded-2xl p-6 shadow-soft">
                  <h3 className="text-lg font-medium text-[#1C1917] mb-4">Pedidos Recientes</h3>
                  {orders.length === 0 ? (
                    <p className="text-[#A8A29E] text-center py-8">No hay pedidos aún</p>
                  ) : (
                    <div className="space-y-4">
                      {orders.slice(0, 5).map((order) => (
                        <div key={order.id} className="flex items-center justify-between py-3 border-b border-[#E7E5E4] last:border-0">
                          <div>
                            <p className="text-sm font-medium text-[#1C1917]">
                              Pedido #{order.id.slice(0, 8)}
                            </p>
                            <p className="text-xs text-[#A8A29E]">
                              {new Date(order.created_at).toLocaleDateString('es-MX')}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-[#1C1917]">
                              {formatPrice(order.total)}
                            </p>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              order.payment_status === 'paid' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {order.payment_status === 'paid' ? 'Pagado' : 'Pendiente'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Products Tab */}
            {activeTab === 'products' && (
              <div data-testid="products-content">
                <div className="flex items-center justify-between mb-8">
                  <h2 
                    className="text-3xl font-light text-[#1C1917]"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    Productos
                  </h2>
                  <button
                    onClick={() => openProductModal()}
                    className="btn-primary flex items-center space-x-2"
                    data-testid="add-product-btn"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Agregar</span>
                  </button>
                </div>

                <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#F5F0EB]">
                        <tr>
                          <th className="text-left px-6 py-4 text-sm font-medium text-[#57534E]">Producto</th>
                          <th className="text-left px-6 py-4 text-sm font-medium text-[#57534E]">Categoría</th>
                          <th className="text-left px-6 py-4 text-sm font-medium text-[#57534E]">Precio</th>
                          <th className="text-left px-6 py-4 text-sm font-medium text-[#57534E]">Stock</th>
                          <th className="text-right px-6 py-4 text-sm font-medium text-[#57534E]">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {products.map((product) => (
                          <tr key={product.id} className="border-t border-[#E7E5E4]" data-testid={`product-row-${product.id}`}>
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-3">
                                <img
                                  src={product.image_url}
                                  alt={product.name}
                                  className="w-12 h-12 rounded-lg object-cover"
                                />
                                <div>
                                  <p className="font-medium text-[#1C1917]">{product.name}</p>
                                  {product.featured && (
                                    <span className="text-xs text-[#BA7B7C]">Destacado</span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-[#57534E]">{product.category}</td>
                            <td className="px-6 py-4 text-sm text-[#1C1917]">{formatPrice(product.price)}</td>
                            <td className="px-6 py-4 text-sm text-[#57534E]">{product.stock}</td>
                            <td className="px-6 py-4">
                              <div className="flex items-center justify-end space-x-2">
                                <button
                                  onClick={() => openProductModal(product)}
                                  className="p-2 text-[#57534E] hover:text-[#BA7B7C] transition-colors"
                                  data-testid={`edit-product-${product.id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteProduct(product.id)}
                                  className="p-2 text-[#57534E] hover:text-red-500 transition-colors"
                                  data-testid={`delete-product-${product.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <div data-testid="orders-content">
                <h2 
                  className="text-3xl font-light text-[#1C1917] mb-8"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Pedidos
                </h2>

                {orders.length === 0 ? (
                  <div className="bg-white rounded-2xl p-12 text-center shadow-soft">
                    <ShoppingCart className="w-12 h-12 mx-auto text-[#E7E5E4] mb-4" />
                    <p className="text-[#A8A29E]">No hay pedidos aún</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div key={order.id} className="bg-white rounded-2xl p-6 shadow-soft" data-testid={`order-${order.id}`}>
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <p className="font-medium text-[#1C1917]">
                              Pedido #{order.order_number || order.id.slice(0, 8)}
                            </p>
                            <p className="text-sm text-[#A8A29E]">
                              {new Date(order.created_at).toLocaleString('es-MX')}
                            </p>
                            {order.user_email && (
                              <p className="text-sm text-[#57534E] mt-1">
                                📧 {order.user_email}
                              </p>
                            )}
                            {order.phone_number && (
                              <a 
                                href={`https://wa.me/${order.phone_number.replace(/[\s+-]/g, '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center space-x-1 text-sm text-[#25D366] hover:underline mt-1"
                              >
                                <span>📱</span>
                                <span>{order.phone_number}</span>
                                <span className="text-xs">(WhatsApp)</span>
                              </a>
                            )}
                          </div>
                          <div className="flex flex-col items-end space-y-2">
                            {/* Payment Method Badge */}
                            {order.payment_method === 'cash_on_delivery' ? (
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                <Banknote className="w-3 h-3 mr-1" />
                                Contra Entrega
                              </span>
                            ) : order.payment_method === 'bank_transfer' ? (
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                <Building2 className="w-3 h-3 mr-1" />
                                Transferencia
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                <CreditCard className="w-3 h-3 mr-1" />
                                Tarjeta
                              </span>
                            )}
                            {/* Payment Status Badge */}
                            <span className={`px-3 py-1 rounded-full text-sm ${
                              order.payment_status === 'paid' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {order.payment_status === 'paid' ? 'Pagado' : 'Pendiente'}
                            </span>
                          </div>
                        </div>
                        
                        <div className="space-y-2 mb-4">
                          {order.items.map((item) => (
                            <div key={`${order.id}-${item.product_id}`} className="flex items-center justify-between text-sm">
                              <span className="text-[#57534E]">
                                {item.name} x {item.quantity}
                              </span>
                              <span className="text-[#1C1917]">{formatPrice(item.subtotal)}</span>
                            </div>
                          ))}
                        </div>
                        
                        {/* Delivery Information */}
                        {order.delivery_method && (
                          <div className="bg-[#FDF8F6] rounded-lg p-3 mb-4">
                            <h5 className="text-xs font-medium text-[#57534E] mb-2 flex items-center">
                              <Truck className="w-3 h-3 mr-1" />
                              ENTREGA
                            </h5>
                            <div className="space-y-1 text-sm">
                              <div className="flex items-center">
                                {order.delivery_method === 'meeting_point' ? (
                                  <>
                                    <MapPin className="w-3 h-3 mr-1 text-[#BA7B7C]" />
                                    <span className="text-[#1C1917]">
                                      {MEETING_POINT_NAMES[order.meeting_point] || order.meeting_point || 'Punto de Encuentro'}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <Home className="w-3 h-3 mr-1 text-[#BA7B7C]" />
                                    <span className="text-[#1C1917]">Envío a Domicilio</span>
                                  </>
                                )}
                              </div>
                              
                              {order.delivery_method === 'shipping' && order.shipping_address && (
                                <div className="text-xs text-[#57534E] ml-4">
                                  {order.shipping_address.street} {order.shipping_address.exterior_number}
                                  {order.shipping_address.interior_number && `, Int. ${order.shipping_address.interior_number}`}
                                  <br />
                                  {order.shipping_address.neighborhood}, {order.shipping_address.municipality}
                                  <br />
                                  {order.shipping_address.state}, C.P. {order.shipping_address.postal_code}
                                  {order.shipping_company && (
                                    <>
                                      <br />
                                      <span className="font-medium">Paquetería:</span> {order.shipping_company}
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        <div className="border-t border-[#E7E5E4] pt-4 flex justify-between items-center">
                          <span className="font-medium text-[#1C1917]">Total</span>
                          <span className="font-medium text-[#BA7B7C] text-lg">{formatPrice(order.total)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>

      {/* Product Modal */}
      {showProductModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowProductModal(false)}></div>
          <div className="relative bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-modal">
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-[#E7E5E4] flex items-center justify-between">
              <h3 className="text-lg font-medium text-[#1C1917]">
                {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
              </h3>
              <button onClick={() => setShowProductModal(false)} className="text-[#57534E]">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveProduct} className="p-6 space-y-4">
              <div>
                <label className="form-label">Nombre</label>
                <input
                  type="text"
                  value={productForm.name}
                  onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                  className="form-input"
                  required
                  data-testid="product-name-input"
                />
              </div>

              <div>
                <label className="form-label">Descripción</label>
                <textarea
                  value={productForm.description}
                  onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                  className="form-input h-24 resize-none"
                  required
                  data-testid="product-description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Precio (MXN)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={productForm.price}
                    onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                    className="form-input"
                    required
                    data-testid="product-price-input"
                  />
                </div>
                <div>
                  <label className="form-label">Stock</label>
                  <input
                    type="number"
                    value={productForm.stock}
                    onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })}
                    className="form-input"
                    required
                    data-testid="product-stock-input"
                  />
                </div>
              </div>

              <div>
                <label className="form-label">Categoría</label>
                <div className="space-y-2">
                  {!showNewCategoryInput ? (
                    <div className="flex space-x-2">
                      <select
                        value={productForm.category}
                        onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                        className="form-input flex-1"
                        data-testid="product-category-select"
                      >
                        {categories.map((cat) => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowNewCategoryInput(true)}
                        className="px-3 py-2 text-sm bg-[#F5F0EB] text-[#57534E] rounded-lg hover:bg-[#E7E5E4] transition-colors whitespace-nowrap"
                        data-testid="add-category-btn"
                      >
                        + Nueva
                      </button>
                    </div>
                  ) : (
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={newCategory}
                        onChange={(e) => setNewCategory(e.target.value)}
                        placeholder="Nombre de categoría"
                        className="form-input flex-1"
                        autoFocus
                        data-testid="new-category-input"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddNewCategory();
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleAddNewCategory}
                        className="px-3 py-2 text-sm bg-[#BA7B7C] text-white rounded-lg hover:bg-[#A36566] transition-colors"
                        data-testid="confirm-category-btn"
                      >
                        Agregar
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowNewCategoryInput(false);
                          setNewCategory('');
                        }}
                        className="px-3 py-2 text-sm bg-[#F5F0EB] text-[#57534E] rounded-lg hover:bg-[#E7E5E4] transition-colors"
                      >
                        Cancelar
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="form-label">URL de imagen</label>
                <input
                  type="url"
                  value={productForm.image_url}
                  onChange={(e) => setProductForm({ ...productForm, image_url: e.target.value })}
                  className="form-input"
                  required
                  data-testid="product-image-input"
                />
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="featured"
                  checked={productForm.featured}
                  onChange={(e) => setProductForm({ ...productForm, featured: e.target.checked })}
                  className="w-4 h-4 rounded border-[#E7E5E4] text-[#BA7B7C] focus:ring-[#BA7B7C]"
                  data-testid="product-featured-checkbox"
                />
                <label htmlFor="featured" className="text-sm text-[#57534E]">
                  Producto destacado
                </label>
              </div>

              <div className="flex space-x-4 pt-4">
                <button type="submit" className="btn-primary flex-1" data-testid="save-product-btn">
                  {editingProduct ? 'Actualizar' : 'Crear'} Producto
                </button>
                <button 
                  type="button" 
                  onClick={() => setShowProductModal(false)} 
                  className="btn-secondary"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-medium text-[#1C1917]">Cambiar Contraseña</h2>
              <button 
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
                }}
                className="text-[#A8A29E] hover:text-[#1C1917]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#57534E] mb-2">
                  Contraseña actual
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? "text" : "password"}
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-[#E7E5E4] focus:border-[#BA7B7C] focus:outline-none pr-12"
                    required
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A8A29E]"
                  >
                    {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#57534E] mb-2">
                  Nueva contraseña
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? "text" : "password"}
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-[#E7E5E4] focus:border-[#BA7B7C] focus:outline-none pr-12"
                    required
                    minLength={6}
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A8A29E]"
                  >
                    {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-[#A8A29E] mt-1">Mínimo 6 caracteres</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#57534E] mb-2">
                  Confirmar nueva contraseña
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? "text" : "password"}
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-[#E7E5E4] focus:border-[#BA7B7C] focus:outline-none pr-12"
                    required
                    minLength={6}
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A8A29E]"
                  >
                    {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
                  }}
                  className="px-6 py-2 text-sm text-[#57534E] hover:bg-[#F5F5F4] rounded-full transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={changingPassword}
                  className="px-6 py-2 bg-[#BA7B7C] text-white text-sm rounded-full hover:bg-[#A36566] transition-colors disabled:opacity-50"
                  data-testid="submit-password-change"
                >
                  {changingPassword ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
